import { useState } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import { getUploadUrl } from '../services/apiService';

const UniversityRequestList = ({ 
  requests, 
  onApprove, 
  onReject, 
  pagination, 
  onPageChange, 
  currentFilter 
}) => {
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'approve', 'reject', or 'details'
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleApproveClick = (request) => {
    setSelectedRequest(request);
    setModalType('approve');
    setAdminNotes('');
    setShowModal(true);
  };

  const handleRejectClick = (request) => {
    setSelectedRequest(request);
    setModalType('reject');
    setRejectionReason('');
    setAdminNotes('');
    setShowModal(true);
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setModalType('details');
    setShowModal(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedRequest) return;

    setProcessing(true);
    try {
      if (modalType === 'approve') {
        await onApprove(selectedRequest._id, adminNotes);
      } else {
        if (!rejectionReason.trim()) {
          alert('Please provide a rejection reason');
          return;
        }
        await onReject(selectedRequest._id, rejectionReason, adminNotes);
      }
      setShowModal(false);
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error processing request:', error);
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (request) => {
    if (request.isVerified) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Approved</span>;
    } else if (request.rejectionReason) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Rejected</span>;
    } else {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Pending</span>;
    }
  };

  // Use the shared getUploadUrl function from apiService
  const getImageSrc = getUploadUrl;

  if (requests.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2-2V7a2 2 0 012-2h4m0 0V3a2 2 0 012 2v2h4a2 2 0 012 2v2M9 7h6m-6 4h6m-6 4h6" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No {currentFilter} requests</h3>
        <p className="mt-1 text-sm text-gray-500">
          {currentFilter === 'pending' ? 'All caught up! No pending university registration requests.' : `No ${currentFilter} requests found.`}
        </p>
      </div>
    );
  }

  // Render Pending Requests as Cards
  if (currentFilter === 'pending') {
    return (
      <div className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {requests.map((request) => (
            <div key={request._id} className="bg-white overflow-hidden shadow-lg rounded-xl border border-gray-200 hover:shadow-xl transition-all duration-300">
              {/* Header with Logo */}
              <div className="relative h-24 bg-gradient-to-r from-[#0e3d1f] to-[#3d7a00]">
                {request.logo && (
                  <div className="absolute -bottom-6 left-6">
                    <img 
                      src={getImageSrc(request.logo)} 
                      alt={`${request.instituteName} logo`}
                      className="w-12 h-12 rounded-full border-4 border-white bg-white object-cover"
                    />
                  </div>
                )}
                <div className="absolute top-4 right-4">
                  {getStatusBadge(request)}
                </div>
              </div>

              <div className="pt-8 p-6">
                <div className="mb-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-1">{request.instituteName}</h3>
                  <p className="text-sm text-gray-600 font-medium">{request.university}</p>
                  <p className="text-sm text-gray-500">{request.instituteCity}, {request.state}</p>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-500 w-20">Contact:</span>
                    <span className="text-sm text-gray-900">{request.salutation} {request.contactPerson}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-500 w-20">Position:</span>
                    <span className="text-sm text-gray-900">{request.status}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-500 w-20">Email:</span>
                    <span className="text-sm text-gray-900 truncate">{request.email}</span>
                  </div>
                </div>

                <div className="mb-4">
                  <span className="text-xs text-gray-500">
                    Submitted {formatDistanceToNow(new Date(request.createdAt), { addSuffix: true })}
                  </span>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => handleViewDetails(request)}
                    className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                  >
                    View Details
                  </button>
                  <button
                    onClick={() => handleApproveClick(request)}
                    className="flex-1 bg-green-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleRejectClick(request)}
                    className="flex-1 bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                  >
                    Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        {renderPagination()}
        {renderModal()}
      </div>
    );
  }

  // Render Approved/Rejected Requests as Table
  return (
    <div className="space-y-6">
      <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className={`${currentFilter === 'approved' ? 'bg-green-50' : 'bg-red-50'}`}>
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Institution
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact Person
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {requests.map((request) => (
                <tr key={request._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {request.logo ? (
                        <img 
                          src={getImageSrc(request.logo)} 
                          alt={`${request.instituteName} logo`}
                          className="w-10 h-10 rounded-full mr-4 object-cover border-2 border-gray-200"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full mr-4 bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-500 text-xs font-medium">
                            {request.instituteName.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-900">{request.instituteName}</div>
                        <div className="text-sm text-gray-500">{request.university}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{request.salutation} {request.contactPerson}</div>
                    <div className="text-sm text-gray-500">{request.status}</div>
                    <div className="text-sm text-gray-500">{request.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{request.instituteCity}</div>
                    <div className="text-sm text-gray-500">{request.state}, {request.country}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(request)}
                    {request.rejectionReason && (
                      <div className="text-xs text-red-600 mt-1 max-w-xs truncate">
                        {request.rejectionReason}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>{format(new Date(request.createdAt), 'MMM dd, yyyy')}</div>
                    <div className="text-xs">{format(new Date(request.createdAt), 'hh:mm a')}</div>
                    {currentFilter === 'approved' && request.approvedAt && (
                      <div className="text-xs text-green-600 mt-1">
                        Approved {format(new Date(request.approvedAt), 'MMM dd')}
                      </div>
                    )}
                    {currentFilter === 'rejected' && request.rejectedAt && (
                      <div className="text-xs text-red-600 mt-1">
                        Rejected {format(new Date(request.rejectedAt), 'MMM dd')}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleViewDetails(request)}
                      className="text-[#0e3d1f] hover:text-[#1d4000] transition-colors"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {renderPagination()}
      {renderModal()}
    </div>
  );

  function renderPagination() {
    if (pagination.total <= 1) return null;

    return (
      <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 rounded-lg shadow">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => onPageChange(pagination.current - 1)}
            disabled={pagination.current === 1}
            className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => onPageChange(pagination.current + 1)}
            disabled={pagination.current === pagination.total}
            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{((pagination.current - 1) * 10) + 1}</span> to{' '}
              <span className="font-medium">{Math.min(pagination.current * 10, pagination.totalRecords)}</span> of{' '}
              <span className="font-medium">{pagination.totalRecords}</span> results
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => onPageChange(pagination.current - 1)}
                disabled={pagination.current === 1}
                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Previous</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                </svg>
              </button>
              
              {[...Array(Math.min(pagination.total, 5))].map((_, index) => {
                let page;
                if (pagination.total <= 5) {
                  page = index + 1;
                } else if (pagination.current <= 3) {
                  page = index + 1;
                } else if (pagination.current >= pagination.total - 2) {
                  page = pagination.total - 4 + index;
                } else {
                  page = pagination.current - 2 + index;
                }
                
                return (
                  <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                      page === pagination.current
                        ? 'z-10 bg-[#0e3d1f] text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0e3d1f]'
                        : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}

              <button
                onClick={() => onPageChange(pagination.current + 1)}
                disabled={pagination.current === pagination.total}
                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Next</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  }

  function renderModal() {
    if (!showModal || !selectedRequest) return null;

    // Details Modal
    if (modalType === 'details') {
      return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-0 border max-w-4xl shadow-lg rounded-lg bg-white">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0e3d1f] to-[#3d7a00] px-6 py-4 rounded-t-lg">
              <div className="flex justify-between items-start">
                <div className="flex items-center">
                  {selectedRequest.logo && (
                    <img 
                      src={getImageSrc(selectedRequest.logo)} 
                      alt={`${selectedRequest.instituteName} logo`}
                      className="w-16 h-16 rounded-full border-4 border-white bg-white object-cover mr-4"
                    />
                  )}
                  <div>
                    <h3 className="text-xl font-medium text-white">{selectedRequest.instituteName}</h3>
                    <p className="text-green-100">{selectedRequest.university}</p>
                    <div className="mt-1">{getStatusBadge(selectedRequest)}</div>
                  </div>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-96 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-3 border-b pb-2">Contact Information</h4>
                    <div className="space-y-2">
                      <div><strong>Contact Person:</strong> {selectedRequest.salutation} {selectedRequest.contactPerson}</div>
                      <div><strong>Position:</strong> {selectedRequest.status}</div>
                      <div><strong>Email:</strong> {selectedRequest.email}</div>
                      <div><strong>Mobile:</strong> {selectedRequest.mobileNumber}</div>
                      {selectedRequest.phoneNumber && <div><strong>Phone:</strong> {selectedRequest.phoneNumber}</div>}
                      {selectedRequest.website && (
                        <div>
                          <strong>Website:</strong> 
                          <a href={selectedRequest.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                            {selectedRequest.website}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-3 border-b pb-2">Address</h4>
                    <div className="space-y-1">
                      <div>{selectedRequest.addressLine1}</div>
                      {selectedRequest.addressLine2 && <div>{selectedRequest.addressLine2}</div>}
                      <div>{selectedRequest.city}, {selectedRequest.state} {selectedRequest.zipCode}</div>
                      <div>{selectedRequest.country}</div>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-3 border-b pb-2">Institution Details</h4>
                    <div className="space-y-2">
                      <div><strong>Institute Name:</strong> {selectedRequest.instituteName}</div>
                      <div><strong>University:</strong> {selectedRequest.university}</div>
                      <div><strong>City:</strong> {selectedRequest.instituteCity}</div>
                      {selectedRequest.description && (
                        <div>
                          <strong>Description:</strong>
                          <p className="mt-1 text-gray-600">{selectedRequest.description}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-3 border-b pb-2">Registration Info</h4>
                    <div className="space-y-2">
                      <div><strong>Submitted:</strong> {format(new Date(selectedRequest.createdAt), 'PPpp')}</div>
                      {selectedRequest.approvedAt && (
                        <div><strong>Approved:</strong> {format(new Date(selectedRequest.approvedAt), 'PPpp')}</div>
                      )}
                      {selectedRequest.rejectedAt && (
                        <div><strong>Rejected:</strong> {format(new Date(selectedRequest.rejectedAt), 'PPpp')}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Campus Photo */}
              {selectedRequest.campusPhoto && (
                <div className="mt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-3 border-b pb-2">Campus Photo</h4>
                  <img 
                    src={getImageSrc(selectedRequest.campusPhoto)} 
                    alt={`${selectedRequest.instituteName} campus`}
                    className="w-full h-64 object-cover rounded-lg border"
                  />
                </div>
              )}

              {/* Admin Notes & Rejection Reason */}
              {(selectedRequest.adminNotes || selectedRequest.rejectionReason) && (
                <div className="mt-6 space-y-4">
                  {selectedRequest.rejectionReason && (
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <h4 className="text-lg font-medium text-red-800 mb-2">Rejection Reason</h4>
                      <p className="text-red-700">{selectedRequest.rejectionReason}</p>
                    </div>
                  )}
                  {selectedRequest.adminNotes && (
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="text-lg font-medium text-blue-800 mb-2">Admin Notes</h4>
                      <p className="text-blue-700">{selectedRequest.adminNotes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons for Pending */}
            {currentFilter === 'pending' && (
              <div className="bg-gray-50 px-6 py-4 rounded-b-lg border-t">
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setModalType('approve');
                      setAdminNotes('');
                    }}
                    className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors"
                  >
                    Approve University
                  </button>
                  <button
                    onClick={() => {
                      setModalType('reject');
                      setRejectionReason('');
                      setAdminNotes('');
                    }}
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors"
                  >
                    Reject University
                  </button>
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-6 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Approve/Reject Modal
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
          <div className="mt-3">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {modalType === 'approve' ? 'Approve' : 'Reject'} University Registration
            </h3>
            
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                {selectedRequest.logo && (
                  <img 
                    src={getImageSrc(selectedRequest.logo)} 
                    alt={`${selectedRequest.instituteName} logo`}
                    className="w-10 h-10 rounded-full border-2 border-gray-200 mr-3 object-cover"
                  />
                )}
                <div>
                  <p className="text-sm font-medium text-gray-900">{selectedRequest.instituteName}</p>
                  <p className="text-sm text-gray-600">{selectedRequest.contactPerson}</p>
                </div>
              </div>
            </div>

            {modalType === 'reject' && (
              <div className="mb-4">
                <label htmlFor="rejectionReason" className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason *
                </label>
                <select
                  id="rejectionReason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0e3d1f] focus:border-[#0e3d1f]"
                  required
                >
                  <option value="">Select a reason</option>
                  <option value="Incomplete information">Incomplete information</option>
                  <option value="Invalid credentials">Invalid credentials</option>
                  <option value="Duplicate registration">Duplicate registration</option>
                  <option value="Not an authorized representative">Not an authorized representative</option>
                  <option value="Institution not recognized">Institution not recognized</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="adminNotes" className="block text-sm font-medium text-gray-700 mb-2">
                Admin Notes (Optional)
              </label>
              <textarea
                id="adminNotes"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0e3d1f] focus:border-[#0e3d1f]"
                placeholder="Add any additional notes..."
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleConfirmAction}
                disabled={processing || (modalType === 'reject' && !rejectionReason)}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  modalType === 'approve'
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {processing ? 'Processing...' : (modalType === 'approve' ? 'Approve' : 'Reject')}
              </button>
              <button
                onClick={() => setShowModal(false)}
                disabled={processing}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default UniversityRequestList;